/**
 * useContentProtection — comprehensive client-side guardrails to prevent
 * casual users from downloading, screenshotting, or copying GurukulVeda
 * content. Admins and support_staff are exempt so QA + content management
 * still work normally.
 *
 * IMPORTANT NOTE ON SCREENSHOTS:
 *   100% screenshot blocking on the open web is technically impossible. The
 *   only platform that can truly block a screen capture is DRM-protected
 *   video (Netflix, etc.) running on hardware that enforces HDCP. For
 *   HTML/image/text content the OS-level Print Screen / Snipping Tool /
 *   phone-camera approach will always work.
 *
 *   What this hook DOES achieve:
 *     ✓ Disables right-click + "Save image as" / "Save link as"
 *     ✓ Blocks all common keyboard shortcuts (Ctrl+S, Ctrl+P, Ctrl+C, Ctrl+A,
 *       Ctrl+U/I, F12, PrintScreen, Cmd equivalents on Mac)
 *     ✓ Disables text selection and image drag
 *     ✓ Detects dev-tools and obscures the page when they're open
 *     ✓ Hides content on window blur (defeats Snipping Tool / OS screenshot
 *       UIs that need focus stealing)
 *     ✓ Clears clipboard immediately after PrintScreen keyup (browser
 *       permission-gated; works on Chrome/Edge desktop)
 *     ✓ CSP-friendly — no eval, no inline scripts injected
 *
 *   What it does NOT achieve:
 *     ✗ Cannot prevent someone photographing the monitor with a phone
 *     ✗ Cannot block screen recording at the OS level
 *     ✗ Cannot stop a determined attacker using devtools-protocol attached
 *       externally (e.g., Puppeteer with --disable-blink-features)
 *
 *   For those residual cases, the watermark overlay (renders the logged-in
 *   user's email on every image) provides traceability — leaked content
 *   shows exactly whose account it came from.
 */
import { useEffect } from 'react';
import { apiPost } from '@/lib/api';

const KEY_BLOCK = new Set([
  's', 'S',   // Ctrl+S → Save page
  'p', 'P',   // Ctrl+P → Print
  'u', 'U',   // Ctrl+U → View source
  'a', 'A',   // Ctrl+A → Select all (mostly defended by user-select: none, but belt-and-braces)
  'c', 'C',   // Ctrl+C → Copy
  'x', 'X',   // Ctrl+X → Cut
  // Note: we don't block Ctrl+V (paste) — students need to paste assignment text into the chat.
]);

const SHIFT_KEY_BLOCK = new Set([
  'I', 'i',   // Ctrl+Shift+I → DevTools
  'J', 'j',   // Ctrl+Shift+J → DevTools Console
  'C', 'c',   // Ctrl+Shift+C → DevTools Inspect Element
]);

export function useContentProtection(user, enabled = true) {
  useEffect(() => {
    // Bypass entirely for admin / support_staff — content management,
    // syllabus approval, infographic moderation all need full browser
    // access. Also bypass if the user is null (login pages, etc.).
    const role = (user?.role || '').toLowerCase();
    const isProtected = enabled
      && !!user
      && role !== 'admin'
      && role !== 'support_staff';
    if (!isProtected) {
      // Make sure we don't have stale CSS hardening from a previous user
      document.documentElement.removeAttribute('data-gv-protected');
      return undefined;
    }
    document.documentElement.setAttribute('data-gv-protected', '1');

    // Track when we last fired a PrtSc-attempt audit log + user toast so we
    // don't spam the backend / the user on rapid repeat presses.
    let lastScreenshotAttemptAt = 0;
    const SCREENSHOT_ATTEMPT_THROTTLE_MS = 5000;

    // Synchronously hide the page contents the instant a screenshot key is
    // detected. We mutate inline style (not a CSS class) because inline
    // style applies before the browser yields to layout/paint, giving us
    // the best chance of beating the OS-level capture race on slower frames.
    // Also schedules an audit POST + user-visible toast.
    const triggerScreenshotDefense = (vector) => {
      const body = document.body;
      if (body) {
        body.style.visibility = 'hidden';
        // Restore after a beat — enough time for the OS capture to complete
        // against the hidden frame on most setups.
        setTimeout(() => { body.style.visibility = ''; }, 1500);
      }
      document.documentElement.setAttribute('data-gv-blurred', '1');
      setTimeout(() => document.documentElement.removeAttribute('data-gv-blurred'), 1500);

      const now = Date.now();
      if (now - lastScreenshotAttemptAt < SCREENSHOT_ATTEMPT_THROTTLE_MS) return;
      lastScreenshotAttemptAt = now;

      // Screenshot deterrent toast removed (per product decision) — snapshots
      // are already blocked and all content is watermarked with the account
      // email, so no user-facing message is needed. We still audit silently.

      // Fire-and-forget audit log so admins can see who's trying to capture.
      // Endpoint is best-effort; failures are silent (we don't want to lock
      // the UI on this).
      try {
        apiPost('/security/screenshot-attempt', { vector }).catch(() => {});
      } catch { /* ignore */ }
    };

    // 1) Block right-click globally
    const onContextMenu = (e) => {
      // Allow context menu on form inputs so students can still right-click
      // to paste into the chat / answer field. Block everywhere else.
      const tag = (e.target?.tagName || '').toUpperCase();
      const editable = (e.target?.isContentEditable);
      if (!editable && tag !== 'INPUT' && tag !== 'TEXTAREA') {
        e.preventDefault();
        return false;
      }
      return true;
    };

    // 2) Block keyboard shortcuts for save/print/devtools/copy/screen-capture
    const onKeyDown = (e) => {
      const isInput = (e.target?.tagName === 'INPUT' || e.target?.tagName === 'TEXTAREA' || e.target?.isContentEditable);
      const meta = e.ctrlKey || e.metaKey;

      // F12 → DevTools
      if (e.key === 'F12') { e.preventDefault(); return; }

      // PrintScreen — OS captures the frame before JS sees the keydown on
      // most systems, so 100% prevention is impossible. What we DO is:
      //   1. Hide content synchronously via inline style (beats the race on
      //      slower paint pipelines + on Snipping Tool which yields a frame).
      //   2. Clear the clipboard on keyup (covers the keyboard-only PrtSc
      //      path on Windows where the capture lands in clipboard).
      //   3. Show a deterrent toast: every page is watermarked with the
      //      user's email — any leaked screenshot points back to them.
      //   4. Audit-log to backend so admins see attempts.
      if (e.key === 'PrintScreen' || e.code === 'PrintScreen') {
        e.preventDefault();
        triggerScreenshotDefense('printscreen_key');
        return;
      }

      // Windows Snipping Tool shortcut (Win+Shift+S) — Windows usually
      // doesn't expose the Win key as `e.metaKey` in browsers, but Shift+S
      // alone is benign so we instead catch this via the `visibilitychange`
      // / `blur` listeners below (Snipping Tool always steals focus).

      // Ctrl/Cmd + Shift + (I/J/C) → DevTools / Inspect
      if (meta && e.shiftKey && SHIFT_KEY_BLOCK.has(e.key)) {
        e.preventDefault();
        return;
      }

      // Ctrl/Cmd + single-letter shortcuts (block save/print/view-source/copy/cut/select-all)
      // Allow them inside form fields so students can copy/select within their own input.
      if (meta && !e.shiftKey && KEY_BLOCK.has(e.key)) {
        if (isInput && (e.key === 'c' || e.key === 'C' || e.key === 'a' || e.key === 'A' || e.key === 'x' || e.key === 'X')) {
          return; // allow within input/textarea
        }
        e.preventDefault();
      }
    };

    const onKeyUp = (e) => {
      // After PrtScn keyup, try to clear the clipboard so even if the
      // browser somehow let it through, the pasted result is empty.
      // Requires a user gesture, which keyup counts as.
      if (e.key === 'PrintScreen' || e.code === 'PrintScreen') {
        try {
          if (navigator.clipboard?.writeText) {
            navigator.clipboard.writeText('').catch(() => {});
          }
        } catch { /* ignore */ }
      }
    };

    // 3) Block drag-image / drag-link → no "drag the image to desktop" loophole
    const onDragStart = (e) => {
      const tag = (e.target?.tagName || '').toUpperCase();
      if (tag === 'IMG' || tag === 'VIDEO' || tag === 'A') {
        e.preventDefault();
      }
    };

    // 4) Block `copy` and `cut` events globally (outside of form fields).
    // Belt + braces — CSS user-select: none already prevents selection, but
    // some platforms still let drag-select happen.
    const onCopy = (e) => {
      const tag = (e.target?.tagName || '').toUpperCase();
      const isInput = (tag === 'INPUT' || tag === 'TEXTAREA' || e.target?.isContentEditable);
      if (!isInput) e.preventDefault();
    };

    // 5) Hide content on window blur. This is the key defence against
    // OS-level screenshot tools (Snipping Tool, Cmd+Shift+4) that steal
    // focus before capturing — by the time they snapshot, our content
    // is already obscured.
    const onBlur = () => document.documentElement.setAttribute('data-gv-blurred', '1');
    const onFocus = () => document.documentElement.removeAttribute('data-gv-blurred');

    // 5b) `visibilitychange` covers the Windows 10/11 Snipping Tool flow
    // where the overlay marks the tab as hidden before capturing. Also
    // catches Win+Shift+S on Edge/Chrome. We treat any "hidden" transition
    // the same way as a blur — obscure everything until the user returns.
    const onVisibilityChange = () => {
      if (document.visibilityState === 'hidden') {
        document.documentElement.setAttribute('data-gv-blurred', '1');
        // Best-effort audit so admins can see this path being used. Throttled
        // by the same dedupe window as the PrtSc keydown handler.
        triggerScreenshotDefense('visibility_hidden');
      } else {
        document.documentElement.removeAttribute('data-gv-blurred');
      }
    };

    // 6) Naive DevTools detection — when devtools open, the viewport's
    // outer/inner width delta jumps. We then mark the document, and CSS
    // blacks out the content. Not bulletproof (a determined dev can
    // detach devtools to a separate window), but catches casual peeking.
    let devToolsOpen = false;
    const checkDevTools = () => {
      const threshold = 200;
      const widthGap = window.outerWidth - window.innerWidth;
      const heightGap = window.outerHeight - window.innerHeight;
      const opening = widthGap > threshold || heightGap > threshold;
      if (opening !== devToolsOpen) {
        devToolsOpen = opening;
        if (opening) document.documentElement.setAttribute('data-gv-devtools', '1');
        else document.documentElement.removeAttribute('data-gv-devtools');
      }
    };
    const devToolsTimer = setInterval(checkDevTools, 1000);

    // 7) Block beforeprint → kills Ctrl+P even if a browser ignored our keydown.
    const onBeforePrint = (e) => {
      e.preventDefault();
      // eslint-disable-next-line no-console
      console.warn('[GurukulVeda] Printing is disabled for this account.');
    };

    document.addEventListener('contextmenu', onContextMenu);
    document.addEventListener('keydown', onKeyDown);
    document.addEventListener('keyup', onKeyUp);
    document.addEventListener('dragstart', onDragStart);
    document.addEventListener('copy', onCopy);
    document.addEventListener('cut', onCopy);
    document.addEventListener('visibilitychange', onVisibilityChange);
    window.addEventListener('blur', onBlur);
    window.addEventListener('focus', onFocus);
    window.addEventListener('beforeprint', onBeforePrint);

    return () => {
      document.documentElement.removeAttribute('data-gv-protected');
      document.documentElement.removeAttribute('data-gv-blurred');
      document.documentElement.removeAttribute('data-gv-devtools');
      document.removeEventListener('contextmenu', onContextMenu);
      document.removeEventListener('keydown', onKeyDown);
      document.removeEventListener('keyup', onKeyUp);
      document.removeEventListener('dragstart', onDragStart);
      document.removeEventListener('copy', onCopy);
      document.removeEventListener('cut', onCopy);
      document.removeEventListener('visibilitychange', onVisibilityChange);
      window.removeEventListener('blur', onBlur);
      window.removeEventListener('focus', onFocus);
      window.removeEventListener('beforeprint', onBeforePrint);
      clearInterval(devToolsTimer);
    };
  }, [user, enabled]);
}
